import React, { useCallback, useRef, useState } from 'react';
import { Box, Flex, HStack, Text, Image, Spinner, Button } from '@chakra-ui/react';
import { FiImage, FiUploadCloud, FiTrash2, FiRefreshCw, FiMove } from 'react-icons/fi';
import { useToast } from '@chakra-ui/react';
import api, { resolveAssetUrl, parseCoverImage, buildCoverImageValue } from '../../services/api';
import EventCoverFocusModal from './EventCoverFocusModal';

const MAX_BYTES = 10 * 1024 * 1024; // 10MB
const ACCEPTED = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];

/**
 * Eventbrite-style cover photo uploader.
 *
 * Uploads to /api/event-assets (which stores a web-optimized derivative) and
 * reports the returned URL via onChange. The parent stores that URL string on
 * the event — this component never deals with the bytes after upload.
 */
const EventCoverUploader = ({ value, onChange }) => {
  const toast = useToast();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  // When set, the focal-point modal is open. `rawUrl` is the stored (relative)
  // URL; `isFresh` means the cover isn't committed yet (just uploaded), so
  // dismissing the modal still commits it with a centered crop.
  const [editing, setEditing] = useState(null);

  const warn = (description) =>
    toast({ title: "Couldn't add cover", description, status: 'warning', duration: 3000, isClosable: true });

  const handleFile = useCallback(
    async (file) => {
      if (!file) return;
      if (!ACCEPTED.includes(file.type)) {
        warn('Use a JPG, PNG, WEBP, or GIF image.');
        return;
      }
      if (file.size > MAX_BYTES) {
        warn('Image must be 10MB or smaller.');
        return;
      }
      setUploading(true);
      setProgress(0);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post('/api/event-assets', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
          },
        });
        // Don't commit yet — let the user position the focal point first.
        setEditing({
          rawUrl: data.url,
          src: resolveAssetUrl(data.url),
          focal: { x: 0.5, y: 0.5 },
          isFresh: true,
        });
      } catch (err) {
        warn(err?.response?.data?.detail || 'Upload failed. Please try again.');
      } finally {
        setUploading(false);
        setProgress(0);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange],
  );

  const onInputChange = (e) => {
    handleFile(e.target.files?.[0]);
    e.target.value = ''; // allow re-selecting the same file
  };
  const openPicker = () => !uploading && inputRef.current?.click();
  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (uploading) return;
    handleFile(e.dataTransfer.files?.[0]);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    if (!uploading) setDragActive(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };
  const removeCover = (e) => {
    e.stopPropagation();
    onChange?.(null);
  };

  // Re-open the focal picker for an already-committed cover.
  const openAdjust = (e) => {
    e.stopPropagation();
    if (!value) return;
    const parsed = parseCoverImage(value);
    setEditing({
      rawUrl: String(value).split('#')[0],
      src: parsed.src,
      focal: parsed.focal,
      isFresh: false,
    });
  };

  const applyFocal = (focal) => {
    if (editing) onChange?.(buildCoverImageValue(editing.rawUrl, focal));
    setEditing(null);
  };

  const closeFocal = () => {
    // A freshly uploaded cover is still committed (centered) on dismiss so the
    // upload isn't silently lost; adjusting an existing cover just cancels.
    if (editing?.isFresh) onChange?.(buildCoverImageValue(editing.rawUrl, null));
    setEditing(null);
  };

  const { src, objectPosition } = value
    ? parseCoverImage(value)
    : { src: null, objectPosition: '50% 50%' };

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED.join(',')}
        onChange={onInputChange}
        style={{ display: 'none' }}
      />

      {/* Cover dropzone — 2:1, full column width (~748px → ~374px tall at the
          780px form). Uniformity with the card/preview comes from the aspect
          ratio + focal point, NOT the pixel size, so this box can be shrunk
          without changing the crop. TODO(cover-size): to make the editor more
          compact later, cap width here (e.g. maxW="520px" → ~260px tall). */}
      <Box
        role="button"
        onClick={openPicker}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        position="relative"
        borderRadius="12px"
        overflow="hidden"
        cursor={uploading ? 'default' : 'pointer'}
        border="1px solid"
        borderColor={dragActive ? 'rgba(255,255,255,0.45)' : src ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.14)'}
        borderStyle={src ? 'solid' : 'dashed'}
        bg="#171717"
        transition="all 0.15s"
        _hover={{ borderColor: uploading ? undefined : 'rgba(255,255,255,0.3)' }}
        sx={{ aspectRatio: '2 / 1', '&:hover .cover-overlay': { opacity: 1 } }}
      >
        {src && (
          <Image src={src} alt="Event cover" w="100%" h="100%" objectFit="cover" objectPosition={objectPosition} />
        )}

        {/* Uploading state */}
        {uploading ? (
          <Flex position="absolute" inset={0} direction="column" align="center" justify="center" gap={2} bg="rgba(0,0,0,0.55)">
            <Spinner size="md" color="rgba(255,255,255,0.85)" />
            <Text fontSize="xs" color="rgba(255,255,255,0.7)">Uploading… {progress}%</Text>
          </Flex>
        ) : src ? (
          // Hover overlay with change / remove actions
          <Flex
            className="cover-overlay"
            position="absolute"
            inset={0}
            align="center"
            justify="center"
            gap={2}
            bg="rgba(0,0,0,0.5)"
            opacity={0}
            transition="opacity 0.15s"
          >
            <Button
              leftIcon={<FiRefreshCw size={13} />}
              size="sm"
              h="32px"
              borderRadius="full"
              bg="rgba(255,255,255,0.92)"
              color="#0a0a0a"
              fontSize="xs"
              fontWeight="600"
              _hover={{ bg: '#fff' }}
              _focus={{ boxShadow: 'none' }}
              onClick={(e) => { e.stopPropagation(); openPicker(); }}
            >
              Change
            </Button>
            <Button
              leftIcon={<FiMove size={13} />}
              size="sm"
              h="32px"
              borderRadius="full"
              bg="rgba(0,0,0,0.4)"
              color="#fafafa"
              border="1px solid rgba(255,255,255,0.25)"
              fontSize="xs"
              fontWeight="600"
              _hover={{ bg: 'rgba(0,0,0,0.6)' }}
              _focus={{ boxShadow: 'none' }}
              onClick={openAdjust}
            >
              Adjust
            </Button>
            <Button
              leftIcon={<FiTrash2 size={13} />}
              size="sm"
              h="32px"
              borderRadius="full"
              bg="rgba(0,0,0,0.4)"
              color="#fafafa"
              border="1px solid rgba(255,255,255,0.25)"
              fontSize="xs"
              fontWeight="600"
              _hover={{ bg: 'rgba(0,0,0,0.6)' }}
              _focus={{ boxShadow: 'none' }}
              onClick={removeCover}
            >
              Remove
            </Button>
          </Flex>
        ) : (
          // Empty state
          <Flex position="absolute" inset={0} direction="column" align="center" justify="center" gap={2} px={4} textAlign="center">
            <Flex
              w="40px" h="40px" borderRadius="full"
              bg="rgba(255,255,255,0.06)" border="1px solid rgba(255,255,255,0.12)"
              align="center" justify="center" color="rgba(255,255,255,0.6)"
            >
              {dragActive ? <FiUploadCloud size={18} /> : <FiImage size={18} />}
            </Flex>
            <Text fontSize="sm" fontWeight="600" color="#fafafa">
              {dragActive ? 'Drop to upload' : 'Add a cover photo'}
            </Text>
            <Text fontSize="xs" color="rgba(255,255,255,0.4)">
              Drag & drop or click to upload · JPG, PNG, WEBP up to 10MB
            </Text>
          </Flex>
        )}
      </Box>

      {!src && !uploading && (
        <HStack spacing={1.5} mt={2} color="rgba(255,255,255,0.4)">
          <Text fontSize="xs">A great cover makes your event stand out. Recommended 1600×900.</Text>
        </HStack>
      )}

      <EventCoverFocusModal
        isOpen={!!editing}
        imageSrc={editing?.src}
        initialFocal={editing?.focal}
        onApply={applyFocal}
        onClose={closeFocal}
      />
    </Box>
  );
};

export default EventCoverUploader;
